import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { PlayersAPI } from "@/lib/api";
import { orderedSections } from "@/lib/sections";
import ShareStoryButton from "@/components/ShareStoryButton";
import SiteHeader from "@/components/SiteHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { getSport } from "@/lib/sports";
import ShareButton from "@/components/ShareButton";
import ScanReveal from "@/components/ScanReveal";
import GoldenParticles from "@/components/GoldenParticles";
import ImmersiveLightbox from "@/components/ImmersiveLightbox";
import { FullPageLoader } from "@/components/Loaders";
import {
  Trophy,
  Calendar,
  Ruler,
  Weight,
  Hash,
  ArrowLeft,
  Pencil,
  Flag,
} from "lucide-react";
import { isVideoFileUrl, toPlayableVideoUrl, videoPosterUrl } from "@/lib/video";
import { transparentLogoUrl } from "@/lib/logo";

const GOLD = "#D4AF37";

const ease = [0.22, 1, 0.36, 1];

const StatBlock = ({ label, value, idx = 0, accentColor }) => {
  const v = String(value ?? "");
  const sizeClass =
    v.length >= 8
      ? "text-lg sm:text-xl md:text-3xl"
      : v.length >= 6
        ? "text-xl sm:text-2xl md:text-4xl"
        : v.length >= 4
          ? "text-2xl sm:text-3xl md:text-5xl"
          : "text-3xl sm:text-4xl md:text-6xl";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay: idx * 0.06, ease }}
      whileHover={{ y: -2 }}
      className="relative metal-card rounded-2xl p-3 sm:p-5 md:p-6 overflow-hidden min-w-0"
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="text-overline mb-2 text-[9px] sm:text-[11px] tracking-[0.12em] leading-tight normal-case sm:uppercase">
        {label}
      </div>
      <div
        className={`font-display ${sizeClass} font-semibold leading-none tracking-tighter whitespace-nowrap text-white`}
      >
        {v}
      </div>
      {/* thin gold accent line */}
      <span
        className="absolute left-4 right-4 bottom-2 h-px opacity-50"
        style={{
          background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
        }}
      />
    </motion.div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
    <div className="flex items-center gap-3 text-zinc-400 font-body text-sm">
      <Icon className="w-4 h-4" style={{ color: GOLD, strokeWidth: 1.5 }} />
      <span className="uppercase tracking-[0.18em] text-xs">{label}</span>
    </div>
    <div className="font-display text-lg font-semibold text-white">
      {value || "—"}
    </div>
  </div>
);

const HighlightEmbed = ({ h }) => {
  const url = h.url || "";
  let embedUrl = url;
  if (h.type === "youtube" || /youtube\.com|youtu\.be/.test(url)) {
    const m = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/,
    );
    if (m) embedUrl = `https://www.youtube.com/embed/${m[1]}`;
  } else if (h.type === "vimeo" || /vimeo\.com/.test(url)) {
    const m = url.match(/vimeo\.com\/(\d+)/);
    if (m) embedUrl = `https://player.vimeo.com/video/${m[1]}`;
  }
  const isVideoFile = isVideoFileUrl(url, h.type);

  return (
    <div className="relative aspect-video w-full rounded-2xl overflow-hidden border border-white/10 bg-black gold-rim">
      {isVideoFile ? (
        <video
          src={toPlayableVideoUrl(url)}
          controls
          playsInline
          preload="metadata"
          poster={h.poster || videoPosterUrl(url) || undefined}
          className="w-full h-full object-cover"
          data-testid="highlight-video-file"
        />
      ) : (
        <iframe
          src={embedUrl}
          title={h.title || "highlight"}
          className="w-full h-full"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          data-testid="highlight-iframe"
        />
      )}
    </div>
  );
};

export default function PlayerProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    let mounted = true;
    PlayersAPI.get(slug)
      .then((d) => mounted && setPlayer(d))
      .catch(() => mounted && setError("Joueur introuvable"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) return <FullPageLoader caption="Chargement du joueur…" />;

  if (error || !player)
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <SiteHeader />
        <div
          className="max-w-5xl mx-auto w-full px-4 md:px-8 py-24 text-center"
          data-testid="not-found"
        >
          <div className="font-display text-4xl font-semibold uppercase mb-3 tracking-tight">
            Joueur introuvable
          </div>
          <p className="text-zinc-400 mb-6">
            Cette page de joueur n&apos;existe pas (encore).
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 hover:underline text-[color:var(--gold)]"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-4 h-4" /> Espace administrateur
          </Link>
        </div>
      </div>
    );

  const sportAccent = player.primary_color || GOLD;
  const motor = getSport(player.sport).position_scoring;
  const isPodium = (pos) => /^(1|2|3)(er|e|ᵉ|ère)?$/i.test(String(pos || "").trim());
  const secOrder = orderedSections("player", player.section_order);
  const secStyle = (k) => ({ order: 10 + secOrder.indexOf(k) });

  return (
    <ScanReveal slug={player.slug} kind="player">
      <div className="min-h-screen bg-[#050505] text-white flex flex-col">
        <SiteHeader playerName={player.name} accentColor={GOLD} />

        {/* Hero */}
        <section className="relative">
          <div className="relative aspect-[4/5] sm:aspect-[16/10] md:aspect-[16/8] w-full overflow-hidden">
            {player.cover_image_url && (
              <img
                src={player.cover_image_url}
                alt={`${player.name} cover`}
                className="absolute inset-0 w-full h-full object-cover scale-105"
              />
            )}
            {player.hero_image_url && (
              <img
                src={player.hero_image_url}
                alt={player.name}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: "center 25%" }}
              />
            )}

            {/* Base dark fade */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/55 to-[#050505]/15" />
            {/* Gold ambient glow */}
            <div className="absolute inset-0 pointer-events-none hero-vignette" />
            {/* Subtle sport-tint overlay (10%) */}
            <div
              className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-50"
              style={{
                background: `radial-gradient(120% 80% at 50% 30%, ${sportAccent}1A 0%, rgba(5,5,5,0) 55%)`,
              }}
            />
            {/* Particles drifting in the hero */}
            <GoldenParticles count={14} density="subtle" />

            {/* Top bar: club + share + edit */}
            <div className="absolute top-4 left-4 right-4 md:top-8 md:left-8 md:right-8 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {player.club_logo_url && (
                  <img
                    src={transparentLogoUrl(player.club_logo_url)}
                    alt={player.club_name}
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/5 backdrop-blur-md border border-white/15 object-contain p-1"
                  />
                )}
                <div>
                  <div className="font-display text-sm md:text-base font-semibold uppercase tracking-wider">
                    {player.club_name}
                  </div>
                  <div className="text-overline">Saison {player.season}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ShareButton
                  kind="player"
                  slug={player.slug}
                  title={player.name}
                  accent={GOLD}
                  compact
                />
                <Link
                  to={`/admin/${player.id}`}
                  data-testid="edit-player-link"
                  className={`text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-white inline-flex items-center gap-1.5 backdrop-blur-md bg-black/30 border border-white/10 rounded-full px-3 py-1.5 ${
                    user?.role === "admin" ? "" : "hidden"
                  }`}
                >
                  <Pencil className="w-3 h-3" /> Éditer
                </Link>
              </div>
            </div>

            {/* Name block */}
            <div className="absolute left-0 right-0 bottom-0 px-4 md:px-8 pb-8 md:pb-14">
              <div className="max-w-5xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease, delay: 0.05 }}
                  className="text-overline mb-2"
                >
                  {motor ? "Pilote" : "Joueur"}
                </motion.div>
                <motion.h1
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.85, ease, delay: 0.12 }}
                  className="font-display text-5xl sm:text-7xl md:text-9xl font-semibold uppercase tracking-tighter leading-[0.85] text-white"
                  style={{
                    filter: "drop-shadow(0 6px 22px rgba(212,175,55,0.18))",
                  }}
                  data-testid="player-name"
                >
                  {player.name}
                </motion.h1>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, ease, delay: 0.22 }}
                  className="font-display text-base md:text-2xl uppercase tracking-wider text-zinc-200 mt-3"
                >
                  {player.position}
                  {player.category ? (
                    <span style={{ color: GOLD }}> · {player.category}</span>
                  ) : null}
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="max-w-5xl mx-auto w-full px-4 md:px-8 py-12 md:py-20" style={secStyle("stats")}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, ease }}
            className="flex items-end justify-between mb-6 md:mb-8"
          >
            <h2 className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight">
              Stats
            </h2>
            <div className="flex items-center gap-3 flex-wrap justify-end">
              <ShareStoryButton
                filename={`carte-${player.slug}`}
                card={{
                  type: "player",
                  title: player.name,
                  name: player.name,
                  subtitle: [
                    player.jersey_number != null && player.jersey_number !== ""
                      ? `#${player.jersey_number}`
                      : null,
                    player.position,
                    player.club_name,
                  ]
                    .filter(Boolean)
                    .join(" · "),
                  photo: player.hero_image_url,
                  accent: GOLD,
                  stats: getSport(player.sport).player_stats.map((s) => ({
                    label: s.label,
                    value: player.stats?.[s.key] ?? 0,
                  })),
                  footer: `${window.location.host}/player/${player.slug}`,
                }}
              />
              <span className="text-overline">
                {getSport(player.sport).label} · Saison {player.season}
              </span>
            </div>
          </motion.div>
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
          >
            {getSport(player.sport).player_stats.map((s, i) => (
              <StatBlock
                key={s.key}
                label={s.label}
                value={player.stats?.[s.key] ?? 0}
                accentColor={GOLD}
                idx={i}
              />
            ))}
          </div>
        </section>

        {/* Highlights */}
        {settings?.show_player_videos !== false &&
          player.highlights?.length > 0 && (
            <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-20" style={secStyle("videos")}>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, ease }}
                className="flex items-end justify-between mb-6"
              >
                <h2 className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight">
                  Highlights
                </h2>
                <span className="text-overline">
                  {player.highlights.length} vidéo
                  {player.highlights.length > 1 ? "s" : ""}
                </span>
              </motion.div>
              <div className="space-y-6">
                {player.highlights.map((h, i) => (
                  <motion.div
                    key={h.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.55, ease, delay: i * 0.06 }}
                    data-testid={`highlight-${h.id}`}
                  >
                    <HighlightEmbed h={h} />
                    {h.title && (
                      <div className="mt-3 font-display uppercase tracking-wider text-zinc-300">
                        {h.title}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </section>
          )}

        {/* Bio + Info */}
        <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-20 grid md:grid-cols-3 gap-6 md:gap-8" style={secStyle("profile")}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, ease }}
            className="md:col-span-2"
          >
            <h2 className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight mb-5">
              Profil
            </h2>
            {player.bio ? (
              <p
                className="font-body text-base md:text-lg text-zinc-300 leading-relaxed"
                data-testid="player-bio"
              >
                {player.bio}
              </p>
            ) : (
              <p className="font-body text-zinc-500">Aucune bio.</p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, ease, delay: 0.05 }}
            className="metal-card rounded-2xl p-5 md:p-6 gold-rim"
            data-testid="player-info-card"
          >
            <div className="text-overline mb-3">Informations</div>
            {motor && player.helmet_image_url && (
              <div className="mb-4" data-testid="pilot-helmet">
                <div
                  className="w-full aspect-[4/3] rounded-xl overflow-hidden border"
                  style={{ borderColor: "rgba(212,175,55,0.3)" }}
                >
                  <img
                    src={player.helmet_image_url}
                    alt={`Casque de ${player.name}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-2 text-center">
                  Casque officiel
                </div>
              </div>
            )}
            <InfoRow icon={Calendar} label="Né le" value={player.date_of_birth} />
            {motor && (
              <InfoRow icon={Flag} label="Écurie / Team" value={player.club_name || null} />
            )}
            <InfoRow
              icon={Ruler}
              label="Taille"
              value={player.height_cm ? `${player.height_cm} cm` : null}
            />
            <InfoRow
              icon={Weight}
              label="Poids"
              value={player.weight_kg ? `${player.weight_kg} kg` : null}
            />
            <InfoRow
              icon={Hash}
              label={motor ? "N° de course" : "Maillot"}
              value={
                player.jersey_number != null ? `#${player.jersey_number}` : null
              }
            />
          </motion.div>
        </section>

        {/* Palmarès de courses (pilotes auto/moto) */}
        {motor && player.race_results?.length > 0 && (
          <section
            className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-20"
            data-testid="pilot-race-results"
            style={secStyle("race_results")}
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, ease }}
              className="flex items-end justify-between mb-6"
            >
              <h2 className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight">
                Palmarès de courses
              </h2>
              <span className="text-overline" style={{ color: GOLD }}>
                <Flag className="w-3 h-3 inline mr-1" /> {player.race_results.length} course
                {player.race_results.length > 1 ? "s" : ""}
              </span>
            </motion.div>
            <div className="bg-[#111216] border border-white/10 rounded-xl overflow-hidden">
              <div className="grid grid-cols-[70px,1fr,80px,60px] sm:grid-cols-[90px,1.6fr,1fr,110px,80px] gap-2 px-3 sm:px-5 py-3 border-b border-white/10 text-[9px] sm:text-[11px] font-display font-bold uppercase tracking-[0.18em] text-zinc-500">
                <div>Date</div>
                <div>Épreuve</div>
                <div className="hidden sm:block">Discipline</div>
                <div className="text-right">Position</div>
                <div className="text-right">PTS</div>
              </div>
              {player.race_results.map((r, i) => (
                <div
                  key={r.id || i}
                  className="grid grid-cols-[70px,1fr,80px,60px] sm:grid-cols-[90px,1.6fr,1fr,110px,80px] gap-2 px-3 sm:px-5 py-3 sm:py-4 border-b border-white/5 last:border-0 items-center hover:bg-white/[0.02] transition-colors"
                  data-testid={`race-result-${i}`}
                >
                  <div className="text-zinc-500 font-display text-xs sm:text-sm tabular-nums">
                    {r.date}
                  </div>
                  <div className="font-display uppercase tracking-tight font-semibold text-xs sm:text-base leading-tight min-w-0">
                    {r.event}
                    <div className="sm:hidden text-[9px] uppercase tracking-[0.14em] text-zinc-500 font-body normal-case">
                      {r.discipline}
                    </div>
                  </div>
                  <div className="hidden sm:block text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                    {r.discipline}
                  </div>
                  <div
                    className="text-right font-display font-black text-sm sm:text-lg"
                    style={{ color: isPodium(r.position) ? GOLD : "#A1A1AA" }}
                  >
                    {isPodium(r.position) && (
                      <Trophy className="w-3.5 h-3.5 inline mr-1 -mt-0.5" style={{ color: GOLD }} />
                    )}
                    {r.position}
                  </div>
                  <div className="text-right font-display tabular-nums font-bold text-sm sm:text-base" style={{ color: GOLD }}>
                    {r.points ?? "—"}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Achievements / Trophy shelf */}
        {player.achievements?.length > 0 && (
          <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-20" style={secStyle("achievements")}>
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, ease }}
              className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight mb-6"
            >
              Palmarès
            </motion.h2>

            {/* Trophy rail */}
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 mb-8">
              {player.achievements.map((a, i) => (
                <motion.div
                  key={`${a.id}-rail`}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, ease, delay: i * 0.05 }}
                  className="shrink-0 w-[200px] metal-card rounded-2xl p-4 flex items-center gap-3 gold-rim"
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                    style={{
                      background:
                        "radial-gradient(circle at 30% 25%, #F9DF9F 0%, #D4AF37 50%, #997D22 100%)",
                      boxShadow:
                        "inset 0 1px 0 rgba(255,255,255,0.5), 0 4px 16px rgba(212,175,55,0.25)",
                    }}
                  >
                    <Trophy
                      className="w-5 h-5"
                      style={{ color: "#1A0F00", strokeWidth: 1.6 }}
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                      {a.date}
                    </div>
                    <div className="font-display text-sm uppercase tracking-tight text-white line-clamp-2">
                      {a.title}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Detailed list */}
            <div
              className="border-l pl-6 space-y-6"
              style={{ borderColor: "rgba(212,175,55,0.22)" }}
            >
              {player.achievements.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, ease, delay: i * 0.04 }}
                  className="relative"
                  data-testid={`achievement-${a.id}`}
                >
                  <span
                    className="absolute -left-[31px] top-1.5 w-3 h-3 rounded-full"
                    style={{
                      backgroundColor: GOLD,
                      boxShadow: `0 0 14px ${GOLD}`,
                    }}
                  />
                  <div className="text-overline mb-1 flex items-center gap-2">
                    <Trophy className="w-3 h-3" /> {a.date}
                  </div>
                  <div className="font-display text-2xl md:text-3xl font-semibold uppercase tracking-tight text-white">
                    {a.title}
                  </div>
                  {a.description && (
                    <div className="font-body text-zinc-400 mt-1">
                      {a.description}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Gallery */}
        {player.gallery?.length > 0 && (
          <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-20 md:pb-28" style={secStyle("gallery")}>
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, ease }}
              className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight mb-6"
            >
              Galerie
            </motion.h2>
            <div className="hidden md:grid grid-cols-3 gap-4">
              {player.gallery.map((g, i) => (
                <motion.button
                  type="button"
                  key={g.id}
                  onClick={() => setLightboxIndex(i)}
                  initial={{ opacity: 0, scale: 0.96 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, ease, delay: (i % 6) * 0.04 }}
                  className="aspect-square rounded-2xl overflow-hidden border border-white/10 group relative shine-on-hover focus:outline-none focus:ring-2 focus:ring-[color:var(--gold)]"
                  data-testid={`gallery-${g.id}`}
                >
                  <img
                    src={g.url}
                    alt={g.caption || ""}
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                  />
                </motion.button>
              ))}
            </div>
            <div className="md:hidden flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 -mx-4 px-4">
              {player.gallery.map((g, i) => (
                <button
                  type="button"
                  key={g.id}
                  onClick={() => setLightboxIndex(i)}
                  className="snap-start shrink-0 w-[78%] aspect-square rounded-2xl overflow-hidden border border-white/10"
                  data-testid={`gallery-mobile-${g.id}`}
                >
                  <img
                    src={g.url}
                    alt={g.caption || ""}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Lightbox */}
        <ImmersiveLightbox
          items={player.gallery || []}
          openIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          testId="player-gallery-lightbox"
        />

        <footer className="border-t border-white/5 py-8 text-center relative" style={{ order: 99 }}>
          <img
            src="/laser-tech-logo.png"
            alt="Laser Tech"
            className="h-12 mx-auto mb-3 opacity-80"
            style={{ mixBlendMode: "screen" }}
          />
          {user && (
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body"
              data-testid="footer-home-link"
            >
              <ArrowLeft className="w-3 h-3" /> Tous les joueurs
            </Link>
          )}
          <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-zinc-700">
            Laser Tech · Création · Genève
          </div>
        </footer>
      </div>
    </ScanReveal>
  );
}
